var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Link, MenuItem } from '@mui/material';
import moment from 'moment';
import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OrgType, Role, UserStatus } from '../../enum';
import { useUserSessionAPI } from '../app/selectors';
import { ColumnFilterHeader, DataGrid, FallbackComponent, MenuButton, useServerTableFilter, } from '../common';
import { useUsersAPI } from './selectors';
import { DialogMode, ROLE_MAP, USER_TYPE_MAP } from './types';
var getExpiredUserStatus = function (user) {
    var todayUtc = moment.utc().startOf('day');
    var endUtc = moment.utc(user.orgs[0].subscriptionEndDate).startOf('day');
    if (user.versionId !== Role.Superadmin && todayUtc.isAfter(endUtc, 'D')) {
        return UserStatus.Inactive;
    }
    return undefined;
};
export var RowMenu = function (props) {
    var t = useTranslation('manageUser').t;
    var expiredUserStatus = getExpiredUserStatus(props.data);
    var userSession = useUserSessionAPI()[0].data;
    var isCurrentUser = (userSession === null || userSession === void 0 ? void 0 : userSession.id) === props.data.id;
    var disableResetPassword = !!expiredUserStatus || props.data.status === UserStatus.Inactive;
    return (_jsxs(MenuButton, { children: [_jsx(MenuItem, __assign({ onClick: props.handleOpenDialog.bind(null, DialogMode.ResetPassword, props.data), disabled: disableResetPassword }, { children: t('resetPassword') })), _jsx(MenuItem, __assign({ onClick: props.handleOpenDialog.bind(null, DialogMode.ChangeStatus, props.data), disabled: !!expiredUserStatus || isCurrentUser }, { children: t("userAction".concat(expiredUserStatus !== null && expiredUserStatus !== void 0 ? expiredUserStatus : props.data.status)) })), _jsx(MenuItem, __assign({ onClick: props.handleOpenDialog.bind(null, DialogMode.DeleteUser, props.data), disabled: isCurrentUser }, { children: t('deleteUser') }))] }));
};
var makeFilterHeader = function (field, label, getFilterState, getAllValues, applyFilter) {
    return function () { return (_jsx(ColumnFilterHeader, { label: label, filterState: getFilterState(field), allValues: getAllValues(field), onApply: function (update) { return applyFilter(field, update); } })); };
};
var useColumns = function (_a) {
    var handleOpenDialog = _a.handleOpenDialog, platform = _a.platform, getFilterState = _a.getFilterState, getAllValues = _a.getAllValues, applyFilter = _a.applyFilter;
    var t = useTranslation(['manageUser', 'translation']).t;
    var userSession = useUserSessionAPI()[0].data;
    var columns = useMemo(function () { return [
        {
            field: 'id',
            headerName: '',
            flex: 0.5,
            sortable: false,
            renderCell: function (params) { return _jsx(RowMenu, { data: params.row, handleOpenDialog: handleOpenDialog }); },
        },
        {
            field: 'companyName',
            headerName: t('company'),
            flex: 3,
            sortable: false,
            renderHeader: makeFilterHeader('companyName', t('company'), getFilterState, getAllValues, applyFilter),
            renderCell: function (params) { return (_jsx(Link, __assign({ component: "button", title: params.value, onClick: function () {
                    return handleOpenDialog(platform === OrgType.Cinareo ? DialogMode.Company : DialogMode.BillingConfig, params.row);
                } }, { children: params.value }))); },
        },
        {
            field: 'country',
            headerName: t('country'),
            flex: 2.5,
            sortable: false,
            renderHeader: makeFilterHeader('country', t('country'), getFilterState, getAllValues, applyFilter),
        },
        {
            field: 'name',
            headerName: t('name'),
            flex: 3,
            sortable: false,
            renderHeader: makeFilterHeader('name', t('name'), getFilterState, getAllValues, applyFilter),
            renderCell: function (params) {
                return platform === OrgType.Cinareo ? (_jsx(Link, __assign({ component: "button", title: params.value, onClick: function () { return handleOpenDialog(DialogMode.User, params.row); } }, { children: params.value }))) : (params.value);
            },
        },
        {
            field: 'email',
            headerName: t('email'),
            flex: 3,
            sortable: false,
        },
        {
            field: 'subscriptionEndDate',
            headerName: t('endDate'),
            flex: 1.5,
            type: 'date',
            sortable: false,
            valueFormatter: function (params) {
                return platform === OrgType.Cinareo
                    ? moment.utc(params.value).format(t('dateFormat.short', { ns: 'translation' }))
                    : '';
            },
        },
        {
            field: 'versionId',
            headerName: t('licenseTypeTitle'),
            flex: 2,
            sortable: false,
            renderHeader: makeFilterHeader('versionId', t('licenseTypeTitle'), getFilterState, getAllValues, applyFilter),
            valueGetter: function (params) { return t("licenseTypeOption.".concat(ROLE_MAP[params.value])); },
        },
        {
            field: 'userType',
            headerName: t('userTypeTitle'),
            flex: 1.5,
            sortable: false,
            renderHeader: makeFilterHeader('userType', t('userTypeTitle'), getFilterState, getAllValues, applyFilter),
            valueGetter: function (params) { return t("userTypeOption.".concat(USER_TYPE_MAP[params.value])); },
        },
        {
            field: 'status',
            headerName: t('status'),
            flex: 1.5,
            sortable: false,
            renderHeader: makeFilterHeader('status', t('status'), getFilterState, getAllValues, applyFilter),
            valueGetter: function (params) {
                var _a;
                return platform === OrgType.Cinareo
                    ? t("userStatus".concat((_a = getExpiredUserStatus(params.row)) !== null && _a !== void 0 ? _a : params.value))
                    : '';
            },
        },
        {
            field: 'lastLoginTime',
            headerName: t('lastLogin'),
            flex: 2,
            sortable: false,
            valueFormatter: function (params) {
                return params.value && moment(params.value).format(t('dateFormat.long', { ns: 'translation' }));
            },
        },
    ]; }, [handleOpenDialog, platform, t, getFilterState, getAllValues, applyFilter]);
    var excludeIndexes = [];
    if (platform !== OrgType.Cinareo) {
        excludeIndexes.push(0);
    }
    if ((userSession === null || userSession === void 0 ? void 0 : userSession.versionId) !== Role.Superadmin) {
        excludeIndexes.push(1, 2, 5);
    }
    return columns.filter(function (_, index) { return !excludeIndexes.includes(index); });
};
export var UserTable = memo(function (_a) {
    var _b, _c, _d;
    var handleOpenDialog = _a.handleOpenDialog, _e = _a.platform, platform = _e === void 0 ? OrgType.Cinareo : _e, _f = _a.searchQuery, searchQuery = _f === void 0 ? '' : _f, _g = _a.refreshKey, refreshKey = _g === void 0 ? 0 : _g;
    var _h = useUsersAPI(), usersData = _h[0], fetchUsers = _h[1], clearUsersData = _h[2];
    var t = useTranslation(['manageUser', 'translation']).t;
    // Server-side filter/sort config. Company/name are high-cardinality (text); the rest are checklists.
    var filterColumns = useMemo(function () { return [
        { field: 'companyName', filter: 'text', sortable: true },
        { field: 'country', filter: 'enum', sortable: true },
        { field: 'name', filter: 'text', sortable: true },
        {
            field: 'versionId',
            filter: 'enum',
            sortable: true,
            getLabel: function (value) { return t("licenseTypeOption.".concat(ROLE_MAP[Number(value)])); },
        },
        {
            field: 'userType',
            filter: 'enum',
            sortable: true,
            getLabel: function (value) { return t("userTypeOption.".concat(USER_TYPE_MAP[Number(value)])); },
        },
        { field: 'status', filter: 'enum', sortable: true, getLabel: function (value) { return t("userStatus".concat(value)); } },
    ]; }, [t]);
    var _j = useServerTableFilter({ columns: filterColumns, facets: (_b = usersData.data) === null || _b === void 0 ? void 0 : _b.facets, search: searchQuery }), queryParams = _j.queryParams, sortModel = _j.sortModel, paginationModel = _j.paginationModel, onPaginationModelChange = _j.onPaginationModelChange, getFilterState = _j.getFilterState, getAllValues = _j.getAllValues, applyFilter = _j.applyFilter;
    useEffect(function () {
        fetchUsers(undefined, __assign(__assign({}, queryParams), { platform: platform }));
    }, [fetchUsers, queryParams, platform, refreshKey]);
    useEffect(function () { return clearUsersData; }, [clearUsersData]);
    // Flatten platform-specific org fields onto each row for display.
    var rows = useMemo(function () {
        var _a, _b, _c;
        return (_c = (_b = (_a = usersData.data) === null || _a === void 0 ? void 0 : _a.items) === null || _b === void 0 ? void 0 : _b.map(function (user) {
            var _a, _b, _c, _d;
            var filteredOrgs = user.orgs.filter(function (org) { return org.orgType === platform; });
            return __assign(__assign({}, user), { orgs: filteredOrgs, country: (_b = (_a = filteredOrgs[0]) === null || _a === void 0 ? void 0 : _a.country) === null || _b === void 0 ? void 0 : _b.name, companyName: filteredOrgs.map(function (org) { return org.name; }).join(', '), subscriptionEndDate: (_c = filteredOrgs[0]) === null || _c === void 0 ? void 0 : _c.subscriptionEndDate, userType: (_d = user.userOrgs[0]) === null || _d === void 0 ? void 0 : _d.userType });
        }).filter(function (user) { return user.orgs.length; })) !== null && _c !== void 0 ? _c : [];
    }, [usersData.data, platform]);
    var columns = useColumns({ handleOpenDialog: handleOpenDialog, platform: platform, getFilterState: getFilterState, getAllValues: getAllValues, applyFilter: applyFilter });
    return (_jsx(FallbackComponent, { children: _jsx(DataGrid, { columns: columns, rows: usersData.requesting ? [] : rows, rowCount: (_d = (_c = usersData.data) === null || _c === void 0 ? void 0 : _c.total) !== null && _d !== void 0 ? _d : 0, paginationMode: "server", sortingMode: "server", sortModel: sortModel, paginationModel: paginationModel, onPaginationModelChange: onPaginationModelChange, localeText: {
                columnHeaderSortIconLabel: t('table.sortIconLabel', { defaultValue: 'Sort' }),
            }, loading: usersData.requesting !== false, autoHeight: true }) }));
});
